<script setup>
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: the inbox "WhatsApp Flows"
// tab. Shows what the number needs before flows can be built, published and
// sent: flow access on the token, the `flows` webhook, the encryption key
// (Mini App flows only), business verification and phone quality. When flow
// access is missing it offers the existing WhatsApp reauthorize flow.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KaptionWhatsappFlowsAPI from 'dashboard/api/kaptionWhatsappFlows';
import { normalizeReadinessChecks } from 'dashboard/helper/kaptionWhatsappFlows';
import WhatsappReauthorize from './channels/whatsapp/Reauthorize.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t, te } = useI18n();

const checks = ref([]);
const isLoading = ref(false);
const unavailable = ref(false);

const STATE_ICONS = {
  ok: 'i-lucide-circle-check text-n-teal-10',
  missing: 'i-lucide-circle-x text-n-ruby-10',
  warning: 'i-lucide-circle-alert text-n-amber-10',
};

const needsReconnect = computed(() =>
  checks.value.some(c => c.key === 'flow_access' && c.state === 'missing')
);

const checkLabel = key => {
  const path = `KAPTION_WHATSAPP_FLOWS.READINESS.CHECKS.${key}`;
  return te(path) ? t(path) : key;
};

const load = async () => {
  isLoading.value = true;
  unavailable.value = false;
  try {
    const { data } = await KaptionWhatsappFlowsAPI.readiness(props.inbox.id);
    checks.value = normalizeReadinessChecks(data);
  } catch {
    checks.value = [];
    unavailable.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.inbox.id, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-4 mx-8 my-6 max-w-3xl">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('KAPTION_WHATSAPP_FLOWS.READINESS.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('KAPTION_WHATSAPP_FLOWS.READINESS.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        sm
        slate
        faded
        icon="i-lucide-refresh-cw"
        :is-loading="isLoading"
        :label="t('KAPTION_WHATSAPP_FLOWS.READINESS.RECHECK')"
        data-testid="kaption-flows-recheck"
        @click="load"
      />
    </div>

    <div v-if="isLoading && !checks.length" class="flex justify-center py-10">
      <Spinner />
    </div>

    <p
      v-else-if="unavailable"
      class="text-sm text-n-slate-11"
      data-testid="kaption-flows-readiness-unavailable"
    >
      {{ t('KAPTION_WHATSAPP_FLOWS.READINESS.UNAVAILABLE') }}
    </p>

    <ul
      v-else
      class="border divide-y rounded-xl border-n-weak divide-n-weak"
      data-testid="kaption-flows-readiness"
    >
      <li
        v-for="check in checks"
        :key="check.key"
        class="flex items-start gap-3 px-4 py-3"
        :data-testid="`kaption-flows-check-${check.key}`"
      >
        <span
          class="mt-0.5 size-4 shrink-0"
          :class="STATE_ICONS[check.state] || STATE_ICONS.warning"
        />
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-n-slate-12">
            {{ checkLabel(check.key) }}
          </div>
          <p v-if="check.detail" class="text-xs text-n-slate-11">
            {{ check.detail }}
          </p>
          <p
            v-if="check.secondary"
            class="text-[11px] text-n-slate-10 tabular-nums"
            :data-testid="`kaption-flows-check-${check.key}-secondary`"
          >
            {{ check.secondary }}
          </p>
        </div>
        <span class="text-xs text-n-slate-10">
          {{ t(`KAPTION_WHATSAPP_FLOWS.READINESS.STATE.${check.state}`) }}
        </span>
      </li>
    </ul>

    <section
      v-if="needsReconnect"
      class="flex flex-col gap-2"
      data-testid="kaption-flows-reconnect"
    >
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('KAPTION_WHATSAPP_FLOWS.READINESS.WHY_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('KAPTION_WHATSAPP_FLOWS.READINESS.WHY_BODY') }}
      </p>
      <WhatsappReauthorize :inbox="inbox" />
    </section>
  </div>
</template>
